import type {
  ProgramFixedContentRow,
  ProgramProgressCell,
  ProgramScopedContentRow,
} from '../types/domain';
import {
  parseLearningContentWorksheetDocument,
  worksheetDocumentMeaningfulPageCount,
} from '../types/learningContentWorksheetCanvas';
import {
  auditLearningBodySnippet,
  clearWorksheetRefFromLearningContentRow,
} from '../utils/programLearningContentFields';
import { isPersistableUuid } from '../utils/programContentSlideOwner';
import { resolveWorksheetRefIdFromLearningContent } from '../utils/programContentWorksheetOwner';
import {
  resolveWorksheetRefLinkState,
  worksheetRefLinkStateLabel,
  type WorksheetRefLinkState,
} from '../utils/programWorksheetLinkState';
import { supabase } from '../supabaseClient';
import {
  deleteProgramContentWorksheet,
  fetchProgramContentWorksheetPresence,
} from './programContentWorksheetService';
import { upsertProgramFixedContentRows } from './programFixedContentService';
import { upsertProgramProgressCells } from './programProgressService';
import { upsertProgramScopedContentRows } from './programScopedContentService';

export type WorksheetAuditLinkKind = 'linked' | 'orphan_unreferenced_ref';

export type WorksheetRefBindingSourceKind = 'scoped_row' | 'fixed_row' | 'program_cell';

export type WorksheetAuditEntry = {
  selectionKey: string;
  worksheetRefId: string;
  pageCount: number;
  documentBytes: number;
  lastUpdatedAt: string | null;
  linkKind: WorksheetAuditLinkKind;
  linkLabel: string;
  linkState: WorksheetRefLinkState | null;
  contextLabel: string;
  learningSnippet: string;
};

export type ProgramWorksheetAuditParams = {
  workspaceId: string;
  programProgressCells: ProgramProgressCell[];
  scopedRows: ProgramScopedContentRow[];
  fixedRows: ProgramFixedContentRow[];
};

type WorksheetDbRow = {
  worksheet_ref_id: string;
  document_json: unknown;
  updated_at: string | null;
};

type WorksheetRefBinding = {
  refId: string;
  sourceKind: WorksheetRefBindingSourceKind;
  sourceId: string;
  contextLabel: string;
  learningSnippet: string;
};

function chunkIds<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

function learningSnippet(row: ProgramProgressCell | ProgramScopedContentRow | ProgramFixedContentRow): string {
  return auditLearningBodySnippet(row);
}

function cellContextLabel(cell: ProgramProgressCell): string {
  return `${cell.monday_iso} ${cell.weekday_key} #${cell.slot_index}`;
}

function worksheetDocumentJsonByteSize(raw: unknown): number {
  try {
    return new Blob([JSON.stringify(raw ?? {})]).size;
  } catch {
    return String(raw ?? '').length;
  }
}

function collectReferencedWorksheetRefIds(
  cells: ProgramProgressCell[],
  scopedRows: ProgramScopedContentRow[],
  fixedRows: ProgramFixedContentRow[]
): Set<string> {
  const out = new Set<string>();
  const absorb = (row: ProgramProgressCell | ProgramScopedContentRow | ProgramFixedContentRow) => {
    const ref = resolveWorksheetRefIdFromLearningContent(row);
    if (ref) out.add(ref);
  };
  cells.forEach((c) => absorb(c));
  scopedRows.forEach((r) => absorb(r));
  fixedRows.forEach((r) => absorb(r));
  return out;
}

function collectWorksheetRefBindings(
  cells: ProgramProgressCell[],
  scopedRows: ProgramScopedContentRow[],
  fixedRows: ProgramFixedContentRow[]
): WorksheetRefBinding[] {
  const out: WorksheetRefBinding[] = [];
  scopedRows.forEach((row) => {
    const refId = resolveWorksheetRefIdFromLearningContent(row);
    const sourceId = (row.id ?? '').trim();
    if (!refId || !sourceId || !isPersistableUuid(sourceId)) return;
    out.push({
      refId,
      sourceKind: 'scoped_row',
      sourceId,
      contextLabel: `${row.scope_type}|${row.scope_key} #${row.order_index}`,
      learningSnippet: learningSnippet(row),
    });
  });
  fixedRows.forEach((row) => {
    const refId = resolveWorksheetRefIdFromLearningContent(row);
    const sourceId = (row.id ?? '').trim();
    if (!refId || !sourceId || !isPersistableUuid(sourceId)) return;
    out.push({
      refId,
      sourceKind: 'fixed_row',
      sourceId,
      contextLabel: `order #${row.order_index}${row.source_annual_cell_key ? ` · ${row.source_annual_cell_key}` : ''}`,
      learningSnippet: learningSnippet(row),
    });
  });
  cells.forEach((cell) => {
    const refId = resolveWorksheetRefIdFromLearningContent(cell);
    const sourceId = (cell.id ?? '').trim();
    if (!refId || !sourceId || !isPersistableUuid(sourceId)) return;
    out.push({
      refId,
      sourceKind: 'program_cell',
      sourceId,
      contextLabel: cellContextLabel(cell),
      learningSnippet: learningSnippet(cell),
    });
  });
  return out;
}

function bindingsByRef(bindings: WorksheetRefBinding[]): Map<string, WorksheetRefBinding[]> {
  const map = new Map<string, WorksheetRefBinding[]>();
  bindings.forEach((b) => {
    const list = map.get(b.refId) ?? [];
    list.push(b);
    map.set(b.refId, list);
  });
  return map;
}

function joinContextLabels(bindings: WorksheetRefBinding[]): string {
  if (!bindings.length) return '—';
  const labels = bindings.map((b) => `${b.sourceKind} · ${b.contextLabel}`);
  if (labels.length === 1) return labels[0]!;
  return labels.join(' · ');
}

function joinLearningSnippets(bindings: WorksheetRefBinding[]): string {
  if (!bindings.length) return '—';
  const unique = Array.from(new Set(bindings.map((b) => b.learningSnippet)));
  return unique.length === 1 ? unique[0]! : unique.join(' / ');
}

async function fetchWorksheetsByRefIds(workspaceId: string, refIds: string[]): Promise<WorksheetDbRow[]> {
  const sid = workspaceId.trim();
  const ids = Array.from(new Set(refIds.map((id) => id.trim()).filter((id) => isPersistableUuid(id))));
  if (!sid || !ids.length) return [];
  const out: WorksheetDbRow[] = [];
  for (const batch of chunkIds(ids, 40)) {
    const { data, error } = await supabase
      .from('program_content_worksheets')
      .select('worksheet_ref_id, document_json, updated_at')
      .eq('workspace_id', sid)
      .in('worksheet_ref_id', batch);
    if (error) throw new Error(error.message);
    out.push(
      ...((data ?? []) as Array<{
        worksheet_ref_id?: string;
        document_json?: unknown;
        updated_at?: string | null;
      }>).map((row) => ({
        worksheet_ref_id: String(row.worksheet_ref_id ?? '').trim(),
        document_json: row.document_json,
        updated_at: row.updated_at ?? null,
      }))
    );
  }
  return out.filter((row) => row.worksheet_ref_id);
}

async function fetchAllWorkspaceWorksheets(workspaceId: string): Promise<WorksheetDbRow[]> {
  const sid = workspaceId.trim();
  if (!sid) return [];
  const { data, error } = await supabase
    .from('program_content_worksheets')
    .select('worksheet_ref_id, document_json, updated_at')
    .eq('workspace_id', sid);
  if (error) throw new Error(error.message);
  return ((data ?? []) as Array<{
    worksheet_ref_id?: string;
    document_json?: unknown;
    updated_at?: string | null;
  }>)
    .map((row) => ({
      worksheet_ref_id: String(row.worksheet_ref_id ?? '').trim(),
      document_json: row.document_json,
      updated_at: row.updated_at ?? null,
    }))
    .filter((row) => row.worksheet_ref_id && isPersistableUuid(row.worksheet_ref_id));
}

function worksheetRowStats(row: WorksheetDbRow): { pageCount: number; documentBytes: number } {
  const doc = parseLearningContentWorksheetDocument(row.document_json);
  return {
    pageCount: doc ? worksheetDocumentMeaningfulPageCount(doc) : 0,
    documentBytes: worksheetDocumentJsonByteSize(row.document_json),
  };
}

export function emptyWorksheetBindingSelectionKey(
  sourceKind: WorksheetRefBindingSourceKind,
  sourceId: string
): string {
  return `empty-worksheet-ref:${sourceKind}:${sourceId.trim()}`;
}

export function parseEmptyWorksheetBindingSelectionKey(
  key: string
): { sourceKind: WorksheetRefBindingSourceKind; sourceId: string } | null {
  if (!key.startsWith('empty-worksheet-ref:')) return null;
  const parts = key.split(':');
  if (parts.length !== 3) return null;
  const sourceKind = parts[1] as WorksheetRefBindingSourceKind;
  const sourceId = parts[2]?.trim() ?? '';
  if (!sourceId || !isPersistableUuid(sourceId)) return null;
  if (sourceKind !== 'scoped_row' && sourceKind !== 'fixed_row' && sourceKind !== 'program_cell') return null;
  return { sourceKind, sourceId };
}

export function parseWorksheetAuditSelectionKey(key: string): string | null {
  if (!key.startsWith('worksheet:')) return null;
  const refId = key.slice('worksheet:'.length).trim();
  return refId && isPersistableUuid(refId) ? refId : null;
}

async function findEmptyWorksheetBindingsForScope(params: {
  workspaceId: string;
  scopedRows: ProgramScopedContentRow[];
  fixedRows: ProgramFixedContentRow[];
  programProgressCells: ProgramProgressCell[];
}): Promise<WorksheetAuditEntry[]> {
  const sid = params.workspaceId.trim();
  if (!sid) return [];
  const bindings = collectWorksheetRefBindings(
    params.programProgressCells,
    params.scopedRows,
    params.fixedRows
  );
  if (!bindings.length) return [];
  const refIds = Array.from(new Set(bindings.map((b) => b.refId)));
  const presence = await fetchProgramContentWorksheetPresence(sid, refIds);
  const out: WorksheetAuditEntry[] = [];
  bindings.forEach((binding) => {
    if (presence.has(binding.refId)) return;
    out.push({
      selectionKey: emptyWorksheetBindingSelectionKey(binding.sourceKind, binding.sourceId),
      worksheetRefId: binding.refId,
      pageCount: 0,
      documentBytes: 0,
      lastUpdatedAt: null,
      linkKind: 'linked',
      linkState: 'ref_no_document',
      linkLabel: worksheetRefLinkStateLabel('ref_no_document'),
      contextLabel: `${binding.sourceKind} · ${binding.contextLabel}`,
      learningSnippet: binding.learningSnippet,
    });
  });
  return out;
}

async function buildWorksheetAuditEntriesForScope(
  params: ProgramWorksheetAuditParams & {
    includeWorkspaceOrphanRefs?: boolean;
  }
): Promise<WorksheetAuditEntry[]> {
  const { workspaceId, programProgressCells, scopedRows, fixedRows } = params;
  const referencedRefs = collectReferencedWorksheetRefIds(programProgressCells, scopedRows, fixedRows);
  const bindings = collectWorksheetRefBindings(programProgressCells, scopedRows, fixedRows);
  const bindingMap = bindingsByRef(bindings);

  const refIdsToLoad = Array.from(referencedRefs);
  const worksheetRows = await fetchWorksheetsByRefIds(workspaceId, refIdsToLoad);

  if (params.includeWorkspaceOrphanRefs) {
    const allRows = await fetchAllWorkspaceWorksheets(workspaceId);
    const seen = new Set(worksheetRows.map((row) => row.worksheet_ref_id));
    allRows.forEach((row) => {
      if (seen.has(row.worksheet_ref_id)) return;
      if (referencedRefs.has(row.worksheet_ref_id)) return;
      seen.add(row.worksheet_ref_id);
      worksheetRows.push(row);
    });
  }

  const entries: WorksheetAuditEntry[] = worksheetRows.map((row) => {
    const refId = row.worksheet_ref_id;
    const refBindings = bindingMap.get(refId) ?? [];
    const hasRef = referencedRefs.has(refId);
    const stats = worksheetRowStats(row);
    const linkState = resolveWorksheetRefLinkState({
      hasRef,
      hasDocument: true,
    });
    const linkKind: WorksheetAuditLinkKind = hasRef ? 'linked' : 'orphan_unreferenced_ref';
    return {
      selectionKey: `worksheet:${refId}`,
      worksheetRefId: refId,
      pageCount: stats.pageCount,
      documentBytes: stats.documentBytes,
      lastUpdatedAt: row.updated_at,
      linkKind,
      linkState,
      linkLabel: hasRef
        ? worksheetRefLinkStateLabel(linkState)
        : worksheetRefLinkStateLabel(linkState) || '고아 — worksheet_ref_id 없음',
      contextLabel: hasRef ? joinContextLabels(refBindings) : `worksheet_ref ${refId.slice(0, 8)}…`,
      learningSnippet: hasRef ? joinLearningSnippets(refBindings) : '—',
    };
  });

  const emptyBindings = await findEmptyWorksheetBindingsForScope({
    workspaceId,
    scopedRows,
    fixedRows,
    programProgressCells,
  });

  const seen = new Set(entries.map((entry) => entry.selectionKey));
  emptyBindings.forEach((binding) => {
    if (seen.has(binding.selectionKey)) return;
    seen.add(binding.selectionKey);
    entries.push(binding);
  });

  return entries.sort((a, b) => {
    const rank = (entry: WorksheetAuditEntry) => {
      if (entry.linkKind === 'orphan_unreferenced_ref') return 0;
      if (entry.linkState === 'ref_no_document' || entry.linkState === 'document_no_ref') return 1;
      return 2;
    };
    const d = rank(a) - rank(b);
    if (d !== 0) return d;
    return a.selectionKey.localeCompare(b.selectionKey, 'en');
  });
}

export async function fetchWorksheetAuditEntriesForProgramScope(
  params: ProgramWorksheetAuditParams
): Promise<WorksheetAuditEntry[]> {
  return buildWorksheetAuditEntriesForScope({
    ...params,
    includeWorkspaceOrphanRefs: false,
  });
}

export async function fetchWorksheetAuditEntriesForAnnualProgress(
  params: ProgramWorksheetAuditParams
): Promise<WorksheetAuditEntry[]> {
  return buildWorksheetAuditEntriesForScope({
    ...params,
    includeWorkspaceOrphanRefs: true,
  });
}

export async function deleteWorksheetAuditSelections(params: {
  workspaceId: string;
  selectionKeys: string[];
}): Promise<void> {
  const sid = params.workspaceId.trim();
  if (!sid) return;
  const seen = new Set<string>();
  for (const key of params.selectionKeys) {
    const refId = parseWorksheetAuditSelectionKey(key);
    if (!refId || seen.has(refId)) continue;
    seen.add(refId);
    await deleteProgramContentWorksheet(refId, sid);
  }
}

export async function deleteEmptyWorksheetBindingSelections(params: {
  workspaceId: string;
  selectionKeys: string[];
  scopedRows: ProgramScopedContentRow[];
  fixedRows: ProgramFixedContentRow[];
  programProgressCells: ProgramProgressCell[];
}): Promise<void> {
  const sid = params.workspaceId.trim();
  if (!sid || !params.selectionKeys.length) return;

  const scopedById = new Map(params.scopedRows.map((row) => [row.id.trim(), row] as const));
  const fixedById = new Map(params.fixedRows.map((row) => [row.id.trim(), row] as const));
  const cellById = new Map(params.programProgressCells.map((cell) => [cell.id.trim(), cell] as const));

  for (const key of params.selectionKeys) {
    const parsed = parseEmptyWorksheetBindingSelectionKey(key);
    if (!parsed) continue;
    const { sourceKind, sourceId } = parsed;

    if (sourceKind === 'scoped_row') {
      const row = scopedById.get(sourceId);
      if (!row) continue;
      await upsertProgramScopedContentRows([clearWorksheetRefFromLearningContentRow(row)]);
      continue;
    }

    if (sourceKind === 'fixed_row') {
      const row = fixedById.get(sourceId);
      if (!row) continue;
      await upsertProgramFixedContentRows([clearWorksheetRefFromLearningContentRow(row)]);
      continue;
    }

    const cell = cellById.get(sourceId);
    if (!cell) continue;
    await upsertProgramProgressCells([clearWorksheetRefFromLearningContentRow(cell)]);
  }
}
